// Tela de display pra TV na recepcao da loja. Sem header/sidebar — fullscreen.
// Mostra grid 4x3 (12 cards) de conversas atendimento mais recentes.
// Realtime via socket: new_message + conversation_updated reordena/atualiza.
import { useEffect, useMemo, useRef, useState } from 'react';
import { Avatar } from '../../components/Avatar';
import { useSocket } from '../../lib/socket';
import { atendimentoApi } from './lib/api';
import type { Conversa } from './lib/tipos';
import { BadgeEtiqueta } from './componentes/BadgeEtiqueta';

const MAX_CARDS = 12;
const URGENTE_MIN = 15; // sem nossa resposta passa de N min => fundo amarelo (urgente)

function fmtTempoDecorrido(iso: string | null): string {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return 'agora';
  const seg = Math.floor(ms / 1000);
  if (seg < 60) return 'agora';
  const min = Math.floor(seg / 60);
  if (min < 60) return `ha ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `ha ${h}h`;
  const d = Math.floor(h / 24);
  return `ha ${d}d`;
}

function fmtRelogio(d: Date): string {
  return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', hour12: false });
}

export function Telao() {
  const { socket } = useSocket();
  const [conversas, setConversas] = useState<Conversa[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [_tick, setTick] = useState(0); // forca re-render dos tempos decorridos
  const [agora, setAgora] = useState(new Date());
  const piscaIdsRef = useRef<Set<number>>(new Set());

  // Carga inicial
  useEffect(() => {
    let cancelado = false;
    atendimentoApi.listarConversas({ inbox: 'atendimento', status: 'todas', mostrar_arquivadas: false, limit: 60 })
      .then((lista) => {
        if (cancelado) return;
        setConversas(lista);
      })
      .finally(() => { if (!cancelado) setCarregando(false); });
    return () => { cancelado = true; };
  }, []);

  // Tick a cada 30s pra recomputar "ha X min"
  useEffect(() => {
    const t = setInterval(() => { setTick((n) => n + 1); setAgora(new Date()); }, 30_000);
    return () => clearInterval(t);
  }, []);

  // Realtime: socket atualiza
  useEffect(() => {
    if (!socket) return;
    function aoNovaMensagem(data: { conversa_id: number; mensagem: any }) {
      // Pisca o card por 2s pra chamar atencao
      piscaIdsRef.current.add(data.conversa_id);
      setTick((n) => n + 1);
      setTimeout(() => {
        piscaIdsRef.current.delete(data.conversa_id);
        setTick((n) => n + 1);
      }, 2000);
      setConversas((prev) => {
        const m = data.mensagem;
        const i = prev.findIndex((c) => c.id === data.conversa_id);
        const ts = m.criado_em;
        if (i === -1) {
          // Conversa nova ainda nao carregada — recarrega lista
          atendimentoApi.listarConversas({ inbox: 'atendimento', status: 'todas', mostrar_arquivadas: false, limit: 60 })
            .then((lista) => setConversas(lista))
            .catch(() => {});
          return prev;
        }
        const atualizada: Conversa = {
          ...prev[i]!,
          ultima_mensagem_em: ts,
          ultima_mensagem_preview: m.texto?.slice(0, 200) ?? prev[i]!.ultima_mensagem_preview,
          ultima_mensagem_direcao: m.direcao,
          ultima_mensagem_status: m.status,
          nao_lidas: m.direcao === 'inbound' ? (prev[i]!.nao_lidas ?? 0) + 1 : prev[i]!.nao_lidas,
        };
        const sem = prev.filter((_, idx) => idx !== i);
        return [atualizada, ...sem];
      });
    }
    function aoConversaUpdate(data: any) {
      setConversas((prev) => prev.map((c) => c.id === data.id ? { ...c, ...data } : c));
    }

    socket.on('new_message', aoNovaMensagem);
    socket.on('conversation_updated', aoConversaUpdate);
    return () => {
      socket.off('new_message', aoNovaMensagem);
      socket.off('conversation_updated', aoConversaUpdate);
    };
  }, [socket]);

  const cards = useMemo(() => {
    return [...conversas]
      .filter((c) => !c.arquivada)
      .sort((a, b) => {
        const ta = a.ultima_mensagem_em ? new Date(a.ultima_mensagem_em).getTime() : 0;
        const tb = b.ultima_mensagem_em ? new Date(b.ultima_mensagem_em).getTime() : 0;
        return tb - ta;
      })
      .slice(0, MAX_CARDS);
  }, [conversas, _tick]);

  const totalAbertas = conversas.filter((c) => !c.arquivada && c.nao_lidas > 0).length;

  return (
    <div className="min-h-screen bg-gray-900 text-white p-4">
      <header className="flex items-center justify-between mb-4 px-2">
        <h1 className="text-2xl font-bold tracking-tight">Atendimento ao vivo</h1>
        <div className="flex items-center gap-6 text-base">
          <div>
            <span className="opacity-60 mr-1">Aguardando:</span>
            <span className="font-bold text-yellow-300 text-xl">{totalAbertas}</span>
          </div>
          <div className="font-mono text-xl">{fmtRelogio(agora)}</div>
        </div>
      </header>

      {carregando && cards.length === 0 && (
        <div className="text-center text-gray-400 py-20 text-lg">Carregando...</div>
      )}

      {!carregando && cards.length === 0 && (
        <div className="text-center text-gray-400 py-20 text-lg">Sem conversas ativas</div>
      )}

      <div className="grid grid-cols-4 gap-3">
        {cards.map((c) => {
          const ct = c.contato;
          const ultimaInbound = c.ultima_mensagem_direcao === 'inbound';
          const minSemResposta = c.ultima_mensagem_em && ultimaInbound
            ? Math.floor((Date.now() - new Date(c.ultima_mensagem_em).getTime()) / 60000)
            : 0;
          const urgente = minSemResposta >= URGENTE_MIN;
          const piscando = piscaIdsRef.current.has(c.id);

          return (
            <div
              key={c.id}
              className={`rounded-lg p-3 transition-all ${piscando ? 'ring-4 ring-yellow-300' : ''}`}
              style={{
                background: urgente ? '#fde68a' : '#1f2937',
                color: urgente ? '#000' : '#e5e7eb',
                minHeight: 180,
              }}
            >
              <div className="flex items-start gap-3 mb-2">
                <Avatar nome={ct.nome || ct.push_name || ''} fotoUrl={ct.foto_url} tamanho={64} />
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-xl leading-tight truncate">
                    {[ct.nome, ct.sobrenome].filter(Boolean).join(' ') || ct.push_name || ct.telefone || 'Desconhecido'}
                  </p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {ct.etiquetas?.filter((e) => !e.ocultar).slice(0, 2).map((e) => (
                      <BadgeEtiqueta key={e.id} nome={e.nome} cor={e.cor} apelido={e.apelido} />
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between mb-1">
                <span className={`text-base font-semibold ${urgente ? 'text-red-700' : ultimaInbound ? 'text-yellow-300' : 'opacity-60'}`}>
                  {fmtTempoDecorrido(c.ultima_mensagem_em)}
                </span>
                {c.nao_lidas > 0 && (
                  <span className="bg-red-500 text-white text-sm font-bold rounded-full px-2 py-0.5 min-w-[24px] text-center">
                    {c.nao_lidas}
                  </span>
                )}
              </div>

              <p className={`text-sm leading-snug line-clamp-2 ${urgente ? 'text-gray-800' : 'opacity-80'}`}>
                {c.ultima_mensagem_direcao === 'outbound' && <span className="opacity-60">Voce: </span>}
                {c.ultima_mensagem_preview || <span className="opacity-50 italic">sem preview</span>}
              </p>

              {c.usuario_atribuido_nome && (
                <p className={`text-xs mt-2 ${urgente ? 'opacity-70' : 'opacity-50'}`}>
                  Atribuido: <span className="font-medium">{c.usuario_atribuido_login || c.usuario_atribuido_nome}</span>
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
